import { HTMLProps, ReactNode, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import shortid from 'shortid';

import SupportPhone from 'components/common/SupportPhone';

import useEmailsLocales from '../use-emails-locales';
import classes from './RegisterForm.module.scss';


const stripHtml = (value: string) => value.replace(/<[^>]*>/g, '').trim();

const getTextFromTranslation = (value: any, fallback = ''): string => {
  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number') {
    return String(value);
  }

  if (!value) {
    return fallback;
  }

  if (typeof value === 'object') {
    if (typeof value.stringValue === 'string') {
      return value.stringValue;
    }

    if (typeof value.prettyValue === 'string') {
      return stripHtml(value.prettyValue);
    }

    const html = value?.props?.dangerouslySetInnerHTML?.__html;
    if (typeof html === 'string') {
      return stripHtml(html);
    }

    const children = value?.props?.children;
    if (typeof children === 'string' || typeof children === 'number') {
      return String(children);
    }

    if (Array.isArray(children)) {
      const text = children
        .map((child) => getTextFromTranslation(child, ''))
        .join('')
        .trim();

      return text || fallback;
    }
  }

  return fallback;
};


const normalizeLabelPart = (value: string) => value.replace(/\s+/g, ' ').trim();

const getComparableLabel = (value: string) =>
  normalizeLabelPart(value).toLocaleLowerCase().replace(/[\s:;,.!?()[\]{}]+/g, '');

const joinLabelParts = (...parts: string[]) =>
  parts.map(normalizeLabelPart).filter(Boolean).join(' ');

const appendMinLengthLabel = (label: string, minLengthLabel: string) => {
  const base = normalizeLabelPart(label);
  const suffix = normalizeLabelPart(minLengthLabel);

  if (!suffix) {
    return base;
  }

  if (getComparableLabel(base).includes(getComparableLabel(suffix))) {
    return base;
  }

  return joinLabelParts(base, suffix);
};

type Values = {
  email: string;
  parentFirstName: string;
  parentLastName: string;
  parentUsername: string;
  childFirstName: string;
  childLastName: string;
  childUsername: string;
  childYob?: string;
  parentYob?: string;
  agreeReceiveTextMessages: boolean;
  phone?: string;
  messageLocale: string;
  registerLocale: string;
  location?: string;
  onePersonProduct: boolean;
};

type RegisterFormProps = {
  initialValues?: Partial<Values>;
  disabledFields?: Partial<Record<keyof Values, boolean>>;
  locations?: Partial<Array<string>>;
  internationalRegistration?: boolean;
  forcedMessageLocale?: string;
  compactRegistration?: boolean;
  submit: (
    values: Values
  ) => Promise<
    | {
        success: true;
        redirectTo?: string;
      }
    | {
        success: false;
        errors?: Record<string, string>;
        error?: string;
      }
  >;
};

const RegisterForm = ({
  initialValues,
  submit,
  disabledFields,
  locations,
  internationalRegistration = false,
  forcedMessageLocale,
  compactRegistration = false,
}: RegisterFormProps) => {

  const translate = useSelector((state: any) => state.status.translate);
  console.log('RegisterForm initialValues: ',initialValues);
  const currentLanguage = useSelector((state: any) => state.status.lang);
  const localesOptions = useEmailsLocales();

  const {
    watch,
    register,
    handleSubmit,
    setError,
    setValue,
    formState: { errors, isSubmitting },
    control,
  } = useForm<Values>({ defaultValues: initialValues });

  const watchAgreeReceiveTextMessages = watch('agreeReceiveTextMessages');
  const watchParentUsername = watch('parentUsername');
  const watchPhone = watch('phone');
  const { onePersonProduct } = initialValues ?? {};
  const resolvedMessageLocale = String(forcedMessageLocale || initialValues?.messageLocale || currentLanguage?.code || 'en').trim() || 'en';
  const hideRegistrationText = internationalRegistration || compactRegistration;
  const hideMessagingControls = hideRegistrationText || onePersonProduct;

  const tText = (stringNumber: number, fallback = '') => {
    const value = typeof translate === 'function'
      ? translate(stringNumber, fallback, true)
      : fallback;

    return getTextFromTranslation(value, fallback);
  };

  const minLengthLabel = tText(872, '(at least 3 chars)');
  const withMinLengthLabel = (label: string) => appendMinLengthLabel(label, minLengthLabel);

  useEffect(() => {
    if (forcedMessageLocale) {
      setValue('messageLocale', forcedMessageLocale);
    }
  }, [forcedMessageLocale, setValue]);

  useEffect(() => {
    if (!watchAgreeReceiveTextMessages) {
      if (watchPhone) {
        setValue('phone', '');
      }
      return;
    }

    if (!watchPhone || !watchPhone.match(/^\+1/)) {
      setValue('phone', '+1');
    }
  }, [setValue, watchPhone, watchAgreeReceiveTextMessages]);

  const onSubmit = async (values: Values) => {
    const result = await submit({
      ...values,
      messageLocale: resolvedMessageLocale,
      registerLocale: forcedMessageLocale || currentLanguage?.code || 'en',
    });

    if (result.success) {
      result.redirectTo && (window.location.href = result.redirectTo);
      return;
    }

    if (result.errors) {
      if (result.errors._error) {
        alert(result.errors._error);
      }

      Object.entries({ ...result.errors, _error: '' }).forEach(
        ([path, message]) => {
          message &&
            setError(path as keyof Values, { message: message as string });
        }
      );

      return;
    } else {
      alert(result.error || 'Something goes wrong');
    }
  };

  return (
    <div className={classes.wrapper}>
      <h1 className={classes.title}>{translate(866, 'Register')}</h1>
      
      {!hideRegistrationText && (
        <div className={classes.subtitle}>{translate(864)}</div>
      )}
      
      <form onSubmit={handleSubmit(onSubmit)}>
        <div 
          className={classes.row}
          style={hideMessagingControls ? {display: 'none'} : {}}
        >
          <div className={classes.selectLocale}>
            <p>{translate(936)}</p>
            <div>
              {localesOptions.map((opt) => (
                <label key={opt.value}>
                  <input
                    type="radio"
                    value={opt.value}
                    {...register('messageLocale')}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          <SupportPhone className={classes.phone} />
        </div>

        <div className={classes.row}>
          <Field
            label={tText(867, 'Email:')}
            error={errors.email?.message}
            inputProps={{
              readOnly: disabledFields?.email,
              type: 'email',
              ...register('email', { required: tText(695, 'Required') }),
            }}
          />

          {watchAgreeReceiveTextMessages && !hideMessagingControls && (
            <Field
              label={tText(874, 'Phone:')}
              error={errors.phone?.message}
              inputProps={{
                type: 'tel',
                readOnly: disabledFields?.phone,
                ...register('phone', {
                  pattern: {
                    value: /\+1\d{10}/,
                    message: 'Wrong phone number',
                  },
                  required: watchAgreeReceiveTextMessages
                    ? tText(695, 'Required')
                    : '',
                }),
              }}
            />
          )}
        </div>

        {locations && !onePersonProduct && (
          <div className={classes.row}>
            <Controller
              name="location"
              control={control}
              defaultValue=""
              rules={{ required: tText(695, 'Required') }}
              render={({ field, fieldState: { error } }) => (
                <div className={classes.inputGroup}>
                  <label>{tText(1302, 'Select location')}</label>
                  <select {...field}>
                    <option value="">
                      {tText(698, 'Select')}
                    </option>
                    {locations.map((location) => (
                      <option key={location} value={location}>
                        {location}
                      </option>
                    ))}
                  </select>
                  {error && <span className={classes.inputError}>{error?.message}</span>}
                </div>
              )}
            />
          </div>
        )}

        <div 
          className={classes.inputGroup}
          style={hideMessagingControls ? {display: 'none'} : {}}
        >
          <label>
            <input type="checkbox" {...register('agreeReceiveTextMessages')} />
            <span>{translate(937)}</span>
          </label>
        </div>

        <div className={`${classes.row} ${classes.registrationFields}`}>
          <div>
            <Field
              label={withMinLengthLabel(tText(onePersonProduct ? 912 : 868, 'Parent First Name:'))}
              error={errors.parentFirstName?.message}
              inputProps={{
                readOnly: disabledFields?.parentFirstName,
                ...register('parentFirstName', {
                  required: tText(695, 'Required'),
                  minLength: {
                    value: 3,
                    message: 'Min length 3 characters',
                  },
                }),
              }}
            />

            <Field
              label={withMinLengthLabel(tText(onePersonProduct ? 913 : 869, 'Parent Last Name:'))}
              error={errors.parentLastName?.message}
              inputProps={{
                readOnly: disabledFields?.parentLastName,
                ...register('parentLastName', {
                  required: tText(695, 'Required'),
                  minLength: {
                    value: 3,
                    message: 'Min length 3 characters',
                  },
                }),
              }}
            />

            
            <Field
              label={withMinLengthLabel(tText(102, 'Parent Username:'))}
              error={errors.parentUsername?.message}
              inputProps={{
                readOnly: disabledFields?.parentUsername,
                ...register('parentUsername', {
                  required: tText(695, 'Required'),
                  minLength: {
                    value: 3,
                    message: 'Min length 3 characters',
                  },
                }),
              }}
            />
          </div>
          
          <div
            style={onePersonProduct ? {display: 'none'} : {}}
          >
            {!onePersonProduct &&
              <Field
                label={withMinLengthLabel(tText(870, 'Child First Name:'))}
                error={errors.childFirstName?.message}
                inputProps={{
                  readOnly: disabledFields?.childFirstName,
                  ...register('childFirstName', {
                    required: tText(695, 'Required'),
                    minLength: {
                      value: 3,
                      message: 'Min length 3 characters',
                    },               
                  }),               
                }}
              />
            }
            
            {onePersonProduct &&
              <Field
                label={withMinLengthLabel(tText(870, 'Child First Name:'))}
                error={errors.childFirstName?.message}
                inputProps={{
                  readOnly: disabledFields?.childFirstName,
                  ...register('childFirstName', {
                    required: tText(695, 'Required'),
                    minLength: {
                      value: 3,
                      message: 'Min length 3 characters',
                    },  
                    value: "First",              
                  }),               
                }}
              />
            }
            
            {!onePersonProduct &&
              <Field
                label={withMinLengthLabel(tText(871, 'Child Last Name:'))}
                error={errors.childLastName?.message}
                inputProps={{
                  readOnly: disabledFields?.childLastName,
                  ...register('childLastName', {
                    required: tText(695, 'Required'),
                    minLength: {
                      value: 3,
                      message: 'Min length 3 characters',
                    },
                  }),
                }}
              />
            }

            {onePersonProduct &&
              <Field
                label={withMinLengthLabel(tText(871, 'Child Last Name:'))}
                error={errors.childLastName?.message}
                inputProps={{
                  readOnly: disabledFields?.childLastName,
                  ...register('childLastName', {
                    required: tText(695, 'Required'),
                    minLength: {
                      value: 3,
                      message: 'Min length 3 characters',
                    },
                    value: "Last",
                  }),
                }}
              />
            }

            {!onePersonProduct &&
              <Field
                label={withMinLengthLabel(tText(102, 'Child Username:'))}
                error={errors.childUsername?.message}
                inputProps={{
                  readOnly: disabledFields?.childUsername,
                  ...register('childUsername', {
                    required: tText(695, 'Required'),
                    validate: (val) =>
                      val === watchParentUsername
                        ? 'Should be unique'
                        : undefined,
                    minLength: {
                      value: 3,
                      message: 'Min length 3 characters',
                    },
                  }),
                }}
              />
            }

            {onePersonProduct &&
              <Field
                label={withMinLengthLabel(tText(102, 'Child Username:'))}
                error={errors.childUsername?.message}
                inputProps={{
                  readOnly: disabledFields?.childUsername,
                  ...register('childUsername', {
                    required: tText(695, 'Required'),
                    validate: (val) =>
                      val === watchParentUsername
                        ? 'Should be unique'
                        : undefined,
                    minLength: {
                      value: 3,
                      message: 'Min length 3 characters',
                    },
                    value: shortid.generate().slice(0, 6)
                  }),
                }}
              />
            }
          </div>
        </div>

        {!hideRegistrationText && (
          <div className={classes.beforeSubmit}>{translate(865)}</div>
        )}

        <button
          disabled={isSubmitting}
          className={classes.submitBtn}
          type="submit"
        >
          {translate(873, 'Submit')}
        </button>
      </form>
    </div>
  );
};

export default RegisterForm;

const Field = ({
  label,
  error,
  inputProps,
}: {
  label: ReactNode;
  inputProps: HTMLProps<HTMLInputElement>;
  error?: string;
}) => {
  return (
    <div className={classes.inputGroup}>
      <label>{label}</label>
      <input {...inputProps} />
      {error && <span className={classes.inputError}>{error}</span>}
    </div>
  );
};
